import { useEffect, useState } from "react";
import { MessageCircle, Sparkles, ArrowRight } from "lucide-react";
import { buildSms } from "@/lib/sms";
import { useLang } from "@/contexts/LangContext";
import { useSettings } from "@/contexts/SettingsContext";

const FALLBACK_DEALS = [
  { id: "free-120", tag_en: "Tonight", tag_fr: "Ce soir",
    title_en: "Free delivery $120+", title_fr: "Livraison gratuite 120 $+",
    body_en: "Spend over $120 and we waive the delivery fee — every order, every night.",
    body_fr: "Dépassez 120 $ et la livraison est offerte — chaque commande, chaque nuit." },
  { id: "100-bottles", tag_en: "Premium", tag_fr: "Premium",
    title_en: "$100 bottles — text for pricing", title_fr: "Bouteilles 100 $ — textez",
    body_en: "Whisky, cognac, top-shelf rum & tequila. Off-menu pricing on request.",
    body_fr: "Whisky, cognac, rhum et tequila haut de gamme. Prix sur demande." },
  { id: "oz-deals", tag_en: "Bulk", tag_fr: "En vrac",
    title_en: "Oz deals — every strain", title_fr: "Promos onces — toutes les souches",
    body_en: "Indica, Sativa, hybrid. Mix & match available. Message for tonight's drop.",
    body_fr: "Indica, Sativa, hybride. Mix & match. Textez pour les promos du soir." },
  { id: "fast-zone", tag_en: "Fast", tag_fr: "Rapide",
    title_en: "Avg 30-45 min in-zone", title_fr: "30-45 min en zone",
    body_en: "Ottawa & Gatineau core — same-night delivery, 24/7. Text us your postal code.",
    body_fr: "Centre Ottawa et Gatineau — livraison la nuit même, 24/7. Textez votre code postal." },
];

const DEAL_ROTATE_MS = 7500;

export const DailyDeals = () => {
  const { lang, t } = useLang();
  const { deals: live } = useSettings();
  const deals = live && live.length > 0 ? live : FALLBACK_DEALS;
  const [active, setActive] = useState(0);

  useEffect(() => {
    // Functional update keeps us out of "setState directly in effect" territory.
    const id = setInterval(() => setActive((a) => (a + 1) % Math.max(deals.length, 1)), DEAL_ROTATE_MS);
    return () => clearInterval(id);
  }, [deals.length]);

  const copyOf = (d) => ({
    tag: lang === "fr" ? d.tag_fr || d.tag_en : d.tag_en,
    title: lang === "fr" ? d.title_fr || d.title_en : d.title_en,
    body: lang === "fr" ? d.body_fr || d.body_en : d.body_en,
  });

  return (
    <section data-testid="daily-deals-section" id="deals" className="relative mx-auto max-w-7xl px-5 sm:px-8 py-12 sm:py-20">
      <div className="flex items-center gap-2 mb-6">
        <Sparkles size={16} className="text-[#00ff41]" />
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#00ff41]">{t("deals_ribbon")}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-6 items-start">
        <div className="lg:col-span-3 relative overflow-hidden rounded-3xl border border-[#00ff41]/25 bg-gradient-to-br from-[#0c0c0c] to-[#070707] p-6 sm:p-10 min-h-[260px]">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[radial-gradient(circle_at_center,rgba(0,255,65,0.15),rgba(0,255,65,0)_70%)]" />
          {deals.map((d, i) => {
            const copy = copyOf(d);
            return (
              <div key={d.id || i} className={`transition-all duration-500 ${i === active ? "opacity-100 translate-y-0 relative" : "opacity-0 -translate-y-2 absolute inset-0 p-6 sm:p-10 pointer-events-none"}`}>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#00ff41]/15 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.25em] text-[#00ff41]">{copy.tag}</span>
                <h3 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05]">{copy.title}</h3>
                <p className="mt-3 max-w-xl text-neutral-300 leading-relaxed">{copy.body}</p>
                <a data-testid={`deal-text-${d.id}`} href={buildSms({ categoryId: `deal-${d.id}` })} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#00ff41] px-5 py-2.5 font-display font-bold text-black hover:bg-[#00e63a] transition">
                  <MessageCircle size={14} />
                  {t("text_to_order")}
                </a>
              </div>
            );
          })}
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 gap-3">
          <h4 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight">{t("deals_title")}</h4>
          <p className="text-sm text-neutral-400 -mt-1 mb-1">{t("deals_sub")}</p>
          {deals.map((d, i) => {
            const copy = copyOf(d);
            return (
              <button key={d.id || i} data-testid={`deal-card-${d.id}`} onClick={() => setActive(i)} className={`group flex items-start gap-3 rounded-2xl border p-3 text-left transition ${i === active ? "border-[#00ff41]/60 bg-[#00ff41]/5" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00ff41]/15 font-mono text-[10px] text-[#00ff41]">{String(i + 1).padStart(2, "0")}</span>
                <div className="min-w-0">
                  <p className="font-display font-bold leading-tight truncate">{copy.title}</p>
                  <p className="text-xs text-neutral-400 line-clamp-1">{copy.body}</p>
                </div>
                <ArrowRight size={14} className={`mt-1 shrink-0 transition ${i === active ? "text-[#00ff41]" : "text-neutral-500 group-hover:text-white"}`} />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default DailyDeals;
